import React, { useMemo } from 'react';
import { DataTable } from '@scale/scaleui-radix';
import {
  CompassScriptLabelType,
  CompassTransformScript,
  CompassTransformScriptMetadata,
  type CompassScriptLabel,
} from '@scale/genai-compass-shared/cards/transform_script';
import LabelBlockquotes from './LabelBlockquotes';

interface TransformScriptDataTableProps {
  selectedCustomer: string;
  searchQuery: string;
  localScript: CompassTransformScript | undefined;
  onScriptChange: (script: CompassTransformScript | undefined) => void;
}

interface TableRowData {
  id: string;
  script: CompassTransformScript;
  name: string;
  description: string;
  labels: CompassScriptLabel[];
}

const TransformScriptDataTable: React.FC<TransformScriptDataTableProps> = ({
  selectedCustomer,
  searchQuery,
  localScript,
  onScriptChange,
}) => {
  // Prepare table data
  const tableData = useMemo(() => {
    return Object.entries(CompassTransformScriptMetadata).map(([script, metadata]) => ({
      id: script,
      script: script as CompassTransformScript,
      name: metadata.name,
      description: metadata.description,
      labels: metadata.labels,
    }));
  }, []);

  // Filter data based on customer and search query
  const filteredData = useMemo(() => {
    return tableData.filter(item => {
      const matchesCustomer =
        selectedCustomer === 'all' ||
        !selectedCustomer ||
        item.labels.some(
          label =>
            label.type === CompassScriptLabelType.CUSTOMER_NAME && label.value === selectedCustomer,
        );

      const matchesSearch =
        !searchQuery ||
        item.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
        item.description.toLowerCase().includes(searchQuery.toLowerCase()) ||
        item.labels.some(label => label.value.toLowerCase().includes(searchQuery.toLowerCase()));

      return matchesCustomer && matchesSearch;
    });
  }, [tableData, selectedCustomer, searchQuery]);

  const columns = [
    {
      id: 'name',
      accessorKey: 'name',
      header: 'Name',
      size: 200,
      cell: function NameColumnCell({ row }: { row: { original: TableRowData } }) {
        return <div className="font-medium">{row.original.name}</div>;
      },
    },
    {
      id: 'description',
      accessorKey: 'description',
      header: 'Description',
      size: 500,
      cell: function DescriptionColumnCell({ row }: { row: { original: TableRowData } }) {
        const labels = row.original.labels;
        const customerLabels = labels.filter(
          label => label.type === CompassScriptLabelType.CUSTOMER_NAME,
        );
        const actionLabels = labels.filter(
          label => label.type === CompassScriptLabelType.ACTION_TYPE,
        );
        const dangerLabels = labels.filter(
          label => label.type === CompassScriptLabelType.DANGER_LEVEL,
        );
        const projectLabels = labels.filter(
          label => label.type === CompassScriptLabelType.PROJECT_ID,
        );

        return (
          <div className="flex flex-col gap-2">
            <div className="text-gray-600">{row.original.description}</div>
            <LabelBlockquotes
              projectLabels={projectLabels}
              customerLabels={customerLabels}
              actionLabels={actionLabels}
              dangerLabels={dangerLabels}
            />
          </div>
        );
      },
    },
  ];

  // Memoize row selection
  const currentRowSelection = useMemo(
    () =>
      filteredData.reduce((acc, row, index) => {
        acc[index] = localScript === row.script;
        return acc;
      }, {} as Record<string, boolean>),
    [localScript, filteredData],
  );

  const handleSelectionChange = (
    newSelection:
      | Record<string, boolean>
      | ((old: Record<string, boolean>) => Record<string, boolean>),
  ) => {
    const finalSelection =
      typeof newSelection === 'function' ? newSelection(currentRowSelection) : newSelection;

    const selectedIndex = Object.entries(finalSelection).find(([, isSelected]) => isSelected)?.[0];
    if (selectedIndex !== undefined) {
      onScriptChange(filteredData[parseInt(selectedIndex)].script as CompassTransformScript);
    } else {
      onScriptChange(undefined);
    }
  };

  return (
    <DataTable.Root
      className={`max-h-[500px]`}
      columns={columns}
      data={filteredData}
      enableRowSelection={true}
      enableMultiRowSelection={false}
      state={{ rowSelection: currentRowSelection }}
      onRowSelectionChange={handleSelectionChange}
      manualPagination
    >
      {({ table }) => (
        <>
          <DataTable.Table>
            <DataTable.Header>
              {table.getHeaderGroups().map(headerGroup => (
                <DataTable.HeaderGroup key={headerGroup.id}>
                  {headerGroup.headers.map(header => (
                    <DataTable.ColumnHeaderCell key={header.id} header={header}>
                      {header.column.getCanFilter() ? (
                        <DataTable.ColumnHeaderFilter header={header} />
                      ) : null}
                    </DataTable.ColumnHeaderCell>
                  ))}
                </DataTable.HeaderGroup>
              ))}
            </DataTable.Header>
            <DataTable.Body>
              {table.getRowModel().rows.map(row => (
                <DataTable.Row
                  key={row.id}
                  row={row as any}
                  data-testid={`transform-script-row-${row.original.script}`}
                  className={
                    row.getIsSelected()
                      ? 'bg-primary-50 hover:bg-primary-100'
                      : 'hover:bg-neutral-50'
                  }
                >
                  {row.getVisibleCells().map(cell => (
                    <DataTable.Cell key={cell.id} cell={cell} />
                  ))}
                </DataTable.Row>
              ))}
            </DataTable.Body>
          </DataTable.Table>
        </>
      )}
    </DataTable.Root>
  );
};

export default TransformScriptDataTable;
